'use client';
import React, { useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ArrowDownIcon } from '@/icons/ArrowDownIcon';
import InstagramIcon from '@/icons/InstagramIcon';
import FacebookIcon from '@/icons/FacebookIcon';
import { SearchIcon } from '@/icons/SearchIcon';
import YouTubeIcon from '@/icons/YoutubeIcon';
import pulseConfig from '@/pulsex.config';
import { Menu } from '@pulsex/models';
import { Logo } from '@/icons/Logo';
import { cn } from '@pulsex/utils';
import { locale } from '@/locale';
import XIcon from '@/icons/XIcon';
import Link from 'next/link';

import {
    ALAKHBAR_FACEBOOK,
    ALAKHBAR_INSTAGRAM,
    ALAKHBAR_X,
    ALAKHBAR_YOUTUBE,
    JOSEPH_SAMAHA_ARCHIVE_PATH,
} from '@/constants';
import LoginButton from '../Login/Index';
import { ThemeToggle } from '../ThemeToggle/Index';
import { siteHref } from '@/utils/links';

type MenuProps = {
    menus: any;
    status?: string;
    scrolled?: boolean;
    scrollDirection?: string;
    pdfData?: any;
};

const BORDER_RADIUS = 3;

const DeskMenu = ({ menus, status, scrolled = false, scrollDirection, pdfData }: MenuProps) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        const query = searchInputRef.current?.value?.trim();
        if (query && query.length > 2) {
            router.push(`/search?q=${query}`);
        }
    };

    const isNewsPage = pathname === '/Newspaper%20Articles';
    const isNewsPaperDetails =
        pathname.includes('/newspaper/') ||
        decodeURIComponent(pathname).includes(locale.todaysRelease);

    const dailyPdfURL = pdfData?.widgets?.[0]?.posts?.[0]?.publicUrl;

    const formattedDate = new Date()
        .toLocaleDateString(pulseConfig.LANGUAGE === 'ar' ? 'ar-LB' : 'en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            numberingSystem: pulseConfig.LANGUAGE === 'ar' ? 'arab' : 'latn',
        })
        .replace('،', '');

    const renderSubMenu = (subMenus: any[], color: string, index: number) => (
        <div
            className={cn(
                'absolute right-0 top-full border !border-t-0 !border-b-0 rtl:!border-l-greyBackgroundDim/35 ltr:!border-r-greyBackgroundDim/35 bg-lightgreyBackground z-10',
                'flex-col space-y-2 transition-opacity opacity-0 hidden w-full text-black',
                `sub-menu-group/${index}`
            )}
            style={{
                borderColor: color,
                borderRightWidth: '9px',
                borderBottomRightRadius: `${BORDER_RADIUS}px`,
                borderLeftWidth: '0px',
            }}
        >
            {subMenus.map((subItem, idx) => {
                // if (subItem.url == '/ziad') return;
                return (
                    <Link
                        key={idx}
                        href={siteHref(
                            decodeURIComponent(subItem.title) === locale.todaysRelease &&
                                dailyPdfURL
                                ? dailyPdfURL
                                : subItem.url
                        )}
                        aria-label={subItem.title}
                        className={cn(
                            'border-greyBackgroundDim/35 w-full px-1.5 text-black hover:text-primary ltr:text-left border-b pb-2',
                            idx === 0 && 'border-t pt-2'
                        )}
                    >
                        {subItem.title}
                    </Link>
                );
            })}
        </div>
    );

    const renderMenuItems = () => {
        const menuItems = menus?.menus['h-main'] || [];
        return menuItems.map((item: any, index: number) => {
            const isActive = pathname.length > 2 && item.url?.includes(pathname?.split('/')[1]);

            return (
                <div
                    key={index}
                    className={cn(
                        'relative menu-item bg-lightgreyBackground hidden lg:block basis-full flex-col items-start justify-start',
                        `group/item${index}`,
                        isActive && `active color-${item.url?.substring(1)}`
                    )}
                >
                    <div className="flex w-full items-center relative">
                        {item?.title === 'المزيد' ? (
                            <div
                                aria-label={item?.title || ''}
                                className={cn(
                                    'flex text-xl miniDeskMin:text-[1.1rem] miniDeskMax:text-xl flex-1 min-w-fit py-1.5 rtl:pr-1.5 ltr:pl-1.5 menu-item-title justify-start',
                                    item.subMenus &&
                                        item.subMenus?.length > 0 &&
                                        'flex items-center',
                                    pulseConfig.LANGUAGE !== 'ar' && 'flex-row-reverse'
                                )}
                                style={{
                                    borderColor: item?.color || item?.icon || '',
                                    borderRightWidth: '9px',
                                    borderTopRightRadius: `${BORDER_RADIUS}px`,
                                    paddingRight: '5px',
                                }}
                            >
                                {item.title}
                                {item.subMenus && item.subMenus?.length > 0 && (
                                    <ArrowDownIcon
                                        size={12}
                                        className={cn(
                                            'rtl:mr-1 ltr:ml-1 transition-transform duration-75 hover:fill-primary fill-black',
                                            `group-hover/item${index}:rotate-180`
                                        )}
                                    />
                                )}
                            </div>
                        ) : (
                            <Link
                                href={siteHref(item.url)}
                                aria-label={item?.title || ''}
                                className={cn(
                                    'flex cursor-pointer text-xl miniDeskMin:text-[1.1rem] miniDeskMax:text-xl flex-1 min-w-fit py-1.5 rtl:pr-1.5 ltr:pl-1.5 menu-item-title justify-start',
                                    item.subMenus &&
                                        item.subMenus?.length > 0 &&
                                        'flex items-center',
                                    pulseConfig.LANGUAGE !== 'ar' && 'flex-row-reverse'
                                )}
                                style={{
                                    borderColor: item?.color || item?.icon || '',
                                    borderRightWidth: '9px',
                                    borderTopRightRadius: `${BORDER_RADIUS}px`,
                                    paddingRight: '5px',
                                }}
                            >
                                {item.title}
                                {/* {item.subMenus && item.subMenus?.length > 0 && (
                  <ArrowDownIcon
                    size={12}
                    className={cn(
                      "rtl:mr-1 ltr:ml-1 transition-transform duration-75 hover:fill-primary fill-black",
                      `group-hover/item${index}:rotate-180`
                    )}
                  />
                )} */}
                            </Link>
                        )}

                        {item?.title === 'المزيد' &&
                            item.subMenus &&
                            item.subMenus?.length > 0 &&
                            renderSubMenu(item.subMenus, item?.color || item?.icon || '', index)}
                    </div>
                </div>
            );
        });
    };

    return (
        <>
            <LoginButton />

            {/* Search and Top Bar */}
            <div
                className={cn(
                    'container flex justify-between w-full pb-6',
                    (isNewsPage || isNewsPaperDetails) && 'pb-1'
                )}
            >
                <div className="flex items-center justify-between w-full pt-4">
                    <a href="/" aria-label="home">
                        <Logo className="fill-black" size={60} />
                    </a>
                    <form
                        onSubmit={handleSubmit}
                        className="w-1/3 max-lg:w-[40%] max-lg:flex-col max-lg:space-y-2"
                    >
                        <div className="relative flex items-center rounded-full border">
                            <input
                                type="search"
                                ref={searchInputRef}
                                placeholder={locale.searchPlaceHolder}
                                className="placeholder:text-center rounded-full font-amine-bold text-2xl lg:px-6 px-4 py-2 flex-grow focus:outline-none [&::-webkit-search-cancel-button]:appearance-none [&::-ms-clear]:hidden"
                                required
                                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            />
                            <div
                                onClick={() => handleSubmit()}
                                className="text-muted-foreground cursor-pointer p-4 border-r"
                            >
                                <SearchIcon size={24} className="fill-black" />
                            </div>
                        </div>
                    </form>
                    <div className="flex items-center">
                        <ThemeToggle className="ml-2.5 p-1 w-8 h-8" iconSize={18} />
                        <Socials
                            menus={menus}
                            className="rounded-full border p-1 w-8 h-8"
                            iconSize={20}
                        />
                        <a
                            href="https://en.al-akhbar.com/"
                            target="_blank"
                            aria-label="english website"
                            className="leading-3 rounded-full border p-1 w-8 h-8 grid place-items-center text-lg lg:hover:text-masterRed"
                        >
                            EN
                        </a>

                        <a
                            href={JOSEPH_SAMAHA_ARCHIVE_PATH}
                            aria-label="joseph samaha"
                            className="w-fit"
                        >
                            <picture>
                                <source srcSet="/images/samaha.webp" type="image/webp" />
                                <img
                                    src="/images/samaha.png"
                                    alt="samaha"
                                    className={cn(
                                        'brightness-100 h-[70px] select-none mr-5',
                                        pulseConfig.LANGUAGE === 'ar' && 'transform -scale-x-100'
                                    )}
                                    style={{ filter: 'contrast(1.2)' }}
                                    width={60}
                                    height={80}
                                />
                            </picture>
                        </a>
                    </div>
                </div>
            </div>

            {/* Header Navigation */}
            <header
                className={cn(
                    'z-40 duration-500 bg-white',
                    scrollDirection === 'down' && 'sticky top-0'
                )}
            >
                <div className="container">
                    <div className="mx-auto flex flex-col items-stretch justify-center">
                        <div
                            className={cn(
                                'container flex items-center justify-between py-4',
                                pulseConfig.LANGUAGE !== 'ar' && 'flex-row-reverse'
                            )}
                        >
                            <nav className="items-start justify-center text-white flex w-full ml-1">
                                <div className="w-full text-black sm:block lg:flex ltr:lg:flex-row-reverse">
                                    {status === 'pending'
                                        ? ''
                                        : // <MenuSkeleton />
                                          renderMenuItems()}
                                </div>
                            </nav>
                            <Link
                                href={siteHref(dailyPdfURL)}
                                className="text-xl min-w-28 flex items-center justify-end"
                            >
                                <img
                                    src="/images/pdf.png"
                                    alt="PDF"
                                    className="ml-1"
                                    width={20}
                                    height={35}
                                    style={{ filter: 'contrast(1.2)' }}
                                />
                                <span>{locale.todaysRelease}</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default DeskMenu;

const socialUrls = {
    facebook: ALAKHBAR_FACEBOOK,
    instagram: ALAKHBAR_INSTAGRAM,
    twitter: ALAKHBAR_X,
    youtube: ALAKHBAR_YOUTUBE,
} as Record<string, string>;

const Socials = ({
    menus,
    className,
    iconSize = 20,
}: {
    menus: Menu;
    className?: string;
    iconSize?: number;
}) => {
    const socials = menus?.menus?.['h-footer-socials'] ?? [];

    return socials.map((item: any, index: number) => {
        const title = item.title?.toLowerCase();
        const url = item.url;

        const commonProps = {
            key: index,
            url: url || socialUrls[title],
            target: '_blank',
            rel: 'noopener noreferrer',
            'aria-label': `${title} social`,
            className: cn('ml-2.5 h-fit w-fit grid place-items-center', className),
        };

        switch (title) {
            // case "facebook":
            //   return (
            //     <a
            //       key={commonProps.key}
            //       rel={commonProps.rel}
            //       target={commonProps.target}
            //       href={commonProps.url}
            //       className={commonProps.className}
            //       aria-label={commonProps["aria-label"]}
            //     >
            //       <FacebookIcon size={iconSize} className="fill-black" />
            //     </a>
            //   );
            case 'instagram':
                return (
                    <a
                        key={commonProps.key}
                        rel={commonProps.rel}
                        target={commonProps.target}
                        href={commonProps.url}
                        className={commonProps.className}
                        aria-label={commonProps['aria-label']}
                    >
                        <InstagramIcon size={iconSize - 3} className="stroke-black" />
                    </a>
                );
            case 'twitter':
                return (
                    <a
                        key={commonProps.key}
                        rel={commonProps.rel}
                        target={commonProps.target}
                        href={commonProps.url}
                        className={commonProps.className}
                        aria-label={commonProps['aria-label']}
                    >
                        <XIcon size={iconSize - 1} className="fill-black" />
                    </a>
                );
            case 'youtube':
                return (
                    <a
                        key={commonProps.key}
                        rel={commonProps.rel}
                        target={commonProps.target}
                        href={commonProps.url}
                        className={commonProps.className}
                        aria-label={commonProps['aria-label']}
                    >
                        <YouTubeIcon size={iconSize} className="fill-black" />
                    </a>
                );
            default:
                return null;
        }
    });
};
